import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  setDoc,
  doc,
  query,
  where,
  serverTimestamp,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db, secondaryAuth } from "../services/firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
  PAYMENT_STATUS,
  markPaymentSuccess,
  markPaymentFailed,
} from "../services/paymentService";
import { deleteUser } from "../services/userService";
import { createSubscription } from "../services/subscriptionService";
import { getDoc } from "firebase/firestore";
import { PACKAGE_TYPES } from "../services/packageService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const SuperAdminDashboard = ({ mode = "superadmin" }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isTeamView = mode === "team";
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    users: 0,
    companies: 0,
    jobs: 0,
    applications: 0,
    packages: 0,
    subscriptions: 0,
    revenue: 0,
    activeJobs: 0,
    pendingCompanies: 0,
    recentApplications: 0,
    monthlyRevenue: 0,
    teamMembers: 0,
    ads: 0,
    franchises: 0,
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentAds, setRecentAds] = useState([]);
  const [topCompanies, setTopCompanies] = useState([]);
  const [revenueData, setRevenueData] = useState({
    labels: [],
    datasets: [],
  });
  const [agents, setAgents] = useState([]);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showAgentListModal, setShowAgentListModal] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showTeamListModal, setShowTeamListModal] = useState(false);
  const [teamForm, setTeamForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    password: "Team@1234",
  });
  const [teamFormError, setTeamFormError] = useState("");
  const [teamFormLoading, setTeamFormLoading] = useState(false);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [showTeamCredentialsModal, setShowTeamCredentialsModal] =
    useState(false);
  const [newTeamCredentials, setNewTeamCredentials] = useState(null);
  const [agentForm, setAgentForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    password: "Agent@123",
    commissionRate: 10,
  });
  const [agentFormError, setAgentFormError] = useState("");
  const [agentFormLoading, setAgentFormLoading] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [newAgentCredentials, setNewAgentCredentials] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [deletingAgent, setDeletingAgent] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [paymentTab, setPaymentTab] = useState("pending"); // pending, success, failed

  // Payment filtering states
  const [paymentFilterRange, setPaymentFilterRange] = useState("all");
  const [paymentFilterCustomStart, setPaymentFilterCustomStart] = useState("");
  const [paymentFilterCustomEnd, setPaymentFilterCustomEnd] = useState("");
  const [paymentFilterUserType, setPaymentFilterUserType] = useState("all");
  const [paymentSearchQuery, setPaymentSearchQuery] = useState("");

  // Helper function to filter payments based on current filters
  const getFilteredPayments = () => {
    let filtered = [...paymentDetails];

    // Filter by date range
    if (paymentFilterRange !== "all") {
      const now = new Date();
      let startDate = null;

      if (paymentFilterRange === "today") {
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      } else if (paymentFilterRange === "last7") {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (paymentFilterRange === "lastMonth") {
        startDate = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          now.getDate(),
        );
      } else if (paymentFilterRange === "custom" && paymentFilterCustomStart) {
        startDate = new Date(paymentFilterCustomStart);
      }

      if (startDate) {
        filtered = filtered.filter((p) => {
          const paymentDate = p.date;
          if (paymentFilterRange === "custom" && paymentFilterCustomEnd) {
            const endDate = new Date(paymentFilterCustomEnd);
            endDate.setHours(23, 59, 59, 999);
            return paymentDate >= startDate && paymentDate <= endDate;
          }
          return paymentDate >= startDate;
        });
      }
    }

    // Filter by user type
    if (paymentFilterUserType !== "all") {
      filtered = filtered.filter((p) => p.userType === paymentFilterUserType);
    }

    // Filter by search query
    if (paymentSearchQuery) {
      const query = paymentSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.userName?.toLowerCase().includes(query) ||
          p.userEmail?.toLowerCase().includes(query) ||
          p.packageName?.toLowerCase().includes(query),
      );
    }

    return filtered;
  };

  // Export to CSV
  const exportToCSV = () => {
    const filtered = getFilteredPayments();
    if (filtered.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Date",
      "User Name",
      "Email",
      "Phone",
      "User Type",
      "Package",
      "Amount",
      "Status",
    ];
    const rows = filtered.map((p) => [
      p.date.toLocaleDateString(),
      p.userName || "N/A",
      p.userEmail || "N/A",
      p.userPhone || "N/A",
      p.userType || "unknown",
      p.packageName || "N/A",
      p.totalAmount || p.amount || 0,
      p.status || "N/A",
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `payments_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  // Export to Excel (using HTML table format)
  const exportToExcel = () => {
    const filtered = getFilteredPayments();
    if (filtered.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Date",
      "User Name",
      "Email",
      "Phone",
      "User Type",
      "Package",
      "Amount",
      "Status",
    ];
    const rows = filtered.map((p) => [
      p.date.toLocaleDateString(),
      p.userName || "N/A",
      p.userEmail || "N/A",
      p.userPhone || "N/A",
      p.userType || "unknown",
      p.packageName || "N/A",
      p.totalAmount || p.amount || 0,
      p.status || "N/A",
    ]);

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head><meta charset="UTF-8"></head>
        <body>
          <table border="1">
            <thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
            <tbody>${rows.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`).join("")}</tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `payments_${new Date().toISOString().split("T")[0]}.xls`;
    link.click();
  };

  const handleApprovePayment = async (payment) => {
    if (
      !window.confirm(
        `Are you sure you want to approve payment for ${payment.userName}?`,
      )
    )
      return;

    setProcessingPayment(payment.id);
    try {
      // 1. Mark payment as success
      const paymentResult = await markPaymentSuccess(payment.id, {
        paymentId: "MANUAL_ADMIN_" + Date.now(),
        orderId: "MANUAL_ADMIN_" + Date.now(),
        signature: "VERIFIED_BY_ADMIN",
      });

      if (!paymentResult.success) throw new Error(paymentResult.error);

      // 2. Fetch Package details to create subscription
      const packageRef = doc(db, "packages", payment.packageId);
      const packageSnap = await getDoc(packageRef);

      let subData = {};

      if (packageSnap.exists()) {
        const pkg = packageSnap.data();
        subData = {
          userId: payment.userId,
          packageId: payment.packageId,
          packageName: pkg.name,
          packageType: pkg.type,
          price: payment.amount,
          duration: pkg.duration || 30,
          features: pkg.features || [],
          maxApplications: pkg.maxApplications || 0,
          maxJobPostings: pkg.maxJobPostings || 0,
          featuredJobs: pkg.featuredJobs || 0,
          maxAds: pkg.maxAds || 0,
          resumeTemplates: pkg.resumeTemplates || 0,
          profileBoost: pkg.profileBoost || false,
          candidateSearch: pkg.candidateSearch || false,
          analyticsAccess: pkg.analyticsAccess || false,
          userType:
            payment.userType === "unknown"
              ? pkg.type === "employer"
                ? "employer"
                : "jobseeker"
              : payment.userType,
          paymentId: payment.id,
          razorpayPaymentId: "MANUAL_ADMIN",
        };
      } else {
        // Fallback if package deleted, try to use payment metadata or defaults
        // This is risky but better than failing completely
        subData = {
          userId: payment.userId,
          packageId: payment.packageId,
          packageName: payment.packageName,
          price: payment.amount,
          duration: 30, // Default fallback
          paymentId: payment.id,
          razorpayPaymentId: "MANUAL_ADMIN",
          // We can't guess limits easily without package data
          maxApplications: 0,
          maxJobPostings: 0,
        };
        alert(
          "Warning: Original package not found. Subscription created with default/minimal values.",
        );
      }

      // 3. Create Subscription
      const subResult = await createSubscription(subData);

      if (!subResult.success)
        throw new Error(
          "Payment marked success but subscription failed: " + subResult.error,
        );

      alert("✅ Payment approved and subscription activated successfully!");

      // Refresh list
      handleViewPaymentDetails();
    } catch (error) {
      console.error(error);
      alert("Error approving payment: " + error.message);
    } finally {
      setProcessingPayment(null);
    }
  };

  const handleRejectPayment = async (payment) => {
    if (
      !window.confirm(
        `Are you sure you want to REJECT payment for ${payment.userName}?`,
      )
    )
      return;

    setProcessingPayment(payment.id);
    try {
      const result = await markPaymentFailed(payment.id, "Rejected by Admin");
      if (result.success) {
        alert("Payment rejected.");
        handleViewPaymentDetails();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error(error);
      alert("Error rejecting payment: " + error.message);
    } finally {
      setProcessingPayment(null);
    }
  };

  const handleViewPaymentDetails = async (e) => {
    if (e) e.preventDefault();
    try {
      // Fetch users for enrichment
      const usersSnap = await getDocs(collection(db, "users"));
      const usersMap = {};
      usersSnap.docs.forEach((doc) => {
        usersMap[doc.id] = doc.data();
      });

      // Fetch ALL payments
      const q = query(collection(db, "payments"));
      const snapshot = await getDocs(q);

      // Enrich payments with user data
      const payments = snapshot.docs
        .map((doc) => {
          const paymentData = doc.data();
          const userData = usersMap[paymentData.userId];
          return {
            id: doc.id,
            ...paymentData,
            date:
              paymentData.createdAt?.toDate?.() ||
              new Date(paymentData.createdAt),
            userType: userData?.userType || "unknown",
            userEmail: userData?.email || paymentData.userEmail || "N/A",
            userPhone: userData?.phone || paymentData.userPhone || "N/A",
          };
        })
        .sort((a, b) => b.date - a.date);

      setPaymentDetails(payments);
      setShowPaymentDetails(true);

      // Default to pending tab if there are pending payments
      const hasPending = payments.some((p) => p.status === "pending");
      setPaymentTab(hasPending ? "pending" : "success");
    } catch (error) {
      console.error("Error fetching payment details:", error);
      alert("Error loading payment details. Please try again.");
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Fetch specific agents query first to reuse
        const agentsQuery = query(
          collection(db, "users"),
          where("userType", "==", "agent"),
        );

        const [
          usersSnap,
          companiesSnap,
          jobsSnap,
          packagesSnap,
          subscriptionsSnap,
          agentsSnap,
          adsSnap,
          paymentsSnap,
          teamSnap,
          franchisesSnap,
        ] = await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(collection(db, "companies")),
          getDocs(collection(db, "jobs")),
          getDocs(collection(db, "packages")),
          getDocs(collection(db, "subscriptions")),
          getDocs(agentsQuery),
          getDocs(collection(db, "ad_banners")),
          getDocs(
            query(
              collection(db, "payments"),
              where("status", "==", PAYMENT_STATUS.SUCCESS),
            ),
          ),
          getDocs(
            query(collection(db, "users"), where("userType", "==", "team")),
          ),
          getDocs(collection(db, "franchises")),
        ]);

        console.log("Stats fetched. Ads found:", adsSnap.size);

        // Process job data
        const jobDocs = jobsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const activeJobs = jobDocs.filter(
          (job) => job.status === "active",
        ).length;

        // Calculate applications from jobs
        const totalApplications = jobDocs.reduce(
          (sum, job) => sum + (job.applicationCount || 0),
          0,
        );

        // Process company data
        const companyDocs = companiesSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const pendingCompanies = companyDocs.filter(
          (company) =>
            company.verificationStatus === "pending" ||
            !company.verificationStatus,
        ).length;

        // Calculate jobs per company for top companies
        const companyJobCounts = {};
        jobDocs.forEach((job) => {
          if (job.companyId) {
            companyJobCounts[job.companyId] =
              (companyJobCounts[job.companyId] || 0) + 1;
          }
        });

        const topCompaniesList = companyDocs
          .map((company) => ({
            ...company,
            jobCount: companyJobCounts[company.id] || 0,
          }))
          .sort((a, b) => b.jobCount - a.jobCount)
          .slice(0, 5);

        // Create a map of users for quick lookup
        const usersMap = {};
        usersSnap.docs.forEach((doc) => {
          usersMap[doc.id] = doc.data();
        });

        // Enrich payments with user data
        const paymentDocs = paymentsSnap.docs.map((doc) => {
          const paymentData = doc.data();
          const userData = usersMap[paymentData.userId];
          return {
            id: doc.id,
            ...paymentData,
            date:
              paymentData.createdAt?.toDate?.() ||
              new Date(paymentData.createdAt),
            userType: userData?.userType || "unknown",
          };
        });

        // Calculate total revenue from payments
        const totalRevenue = paymentDocs.reduce(
          (sum, payment) => sum + (payment.totalAmount || payment.amount || 0),
          0,
        );

        // Calculate monthly revenue (current month)
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const monthlyRevenue = paymentDocs
          .filter((payment) => {
            const paymentDate = payment.date;
            return (
              paymentDate.getMonth() === currentMonth &&
              paymentDate.getFullYear() === currentYear
            );
          })
          .reduce(
            (sum, payment) =>
              sum + (payment.totalAmount || payment.amount || 0),
            0,
          );

        // Calculate revenue graph data (Last 6 Months)
        const last6Months = Array.from({ length: 6 }, (_, i) => {
          const d = new Date();
          d.setMonth(d.getMonth() - i);
          return d;
        }).reverse();

        const monthlyData = last6Months.map((date) => {
          const month = date.getMonth();
          const year = date.getFullYear();
          const label = date.toLocaleString("default", { month: "short" });

          const total = paymentDocs
            .filter((p) => {
              const pDate = p.date;
              return pDate.getMonth() === month && pDate.getFullYear() === year;
            })
            .reduce((sum, p) => sum + (p.totalAmount || p.amount || 0), 0);

          return { label, total };
        });

        setRevenueData({
          labels: monthlyData.map((d) => d.label),
          datasets: [
            {
              label: "Revenue",
              data: monthlyData.map((d) => d.total),
              borderColor: "rgb(80, 141, 12)", // secondary (Green)
              backgroundColor: "rgba(80, 141, 12, 0.1)",
              fill: true,
              tension: 0.4,
            },
          ],
        });

        // Get recent jobs (last 5)
        const recentJobsList = jobDocs
          .sort(
            (a, b) =>
              new Date(b.createdAt?.toDate?.() || b.createdAt || 0) -
              new Date(a.createdAt?.toDate?.() || a.createdAt || 0),
          )
          .slice(0, 5);

        // Process agents with referral counts
        const agentsList = await Promise.all(
          agentsSnap.docs.map(async (doc) => {
            const agentData = { id: doc.id, ...doc.data() };
            const referralsQuery = query(
              collection(db, "users"),
              where("referredBy", "==", agentData.uid || agentData.id),
            );
            const referralsSnap = await getDocs(referralsQuery);
            agentData.referralCount = referralsSnap.size;
            return agentData;
          }),
        );

        // Process Team Members
        const teamList = teamSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Process Recent Ads
        const recentAdsList = adsSnap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const dateA =
              a.createdAt?.toDate?.() ||
              new Date(a.createdAt?.seconds * 1000 || 0);
            const dateB =
              b.createdAt?.toDate?.() ||
              new Date(b.createdAt?.seconds * 1000 || 0);
            return dateB - dateA;
          })
          .slice(0, 5);

        // Set all state
        setStats({
          users: usersSnap.size,
          companies: companiesSnap.size,
          jobs: jobsSnap.size,
          applications: totalApplications,
          packages: packagesSnap.size,
          subscriptions: subscriptionsSnap.size,
          revenue: totalRevenue,
          activeJobs,
          pendingCompanies,
          recentApplications: totalApplications, // kept for backward compatibility if used
          monthlyRevenue,
          teamMembers: teamList.length,
          ads: adsSnap.size,
          franchises: franchisesSnap.size,
        });

        setRecentJobs(recentJobsList);
        setRecentAds(recentAdsList);
        setTopCompanies(topCompaniesList);
        setAgents(agentsList);
        setTeamMembers(teamList);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching stats:", error);
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleTeamFormChange = (e) => {
    const { name, value } = e.target;
    setTeamForm({ ...teamForm, [name]: value });
  };

  const handleAddTeam = async (e) => {
    e.preventDefault();
    setTeamFormLoading(true);
    setTeamFormError("");

    try {
      // Create Firebase Auth user for the team member using secondary auth instance
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        teamForm.email,
        teamForm.password,
      );
      const uid = userCredential.user.uid;

      // Create user document in Firestore
      await setDoc(doc(db, "users", uid), {
        firstName: teamForm.firstName,
        lastName: teamForm.lastName,
        email: teamForm.email,
        phone: teamForm.phone,
        address: teamForm.address,
        userType: "team",
        uid: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Sign out the team member from secondary auth (doesn't affect main auth)
      await secondaryAuth.signOut();

      // Refresh team members list
      const teamSnap = await getDocs(
        query(collection(db, "users"), where("userType", "==", "team")),
      );
      const teamList = teamSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTeamMembers(teamList);

      // Store credentials for display
      setNewTeamCredentials({
        email: teamForm.email,
        password: teamForm.password,
        name: `${teamForm.firstName} ${teamForm.lastName}`,
      });

      // Reset form and close modal
      setTeamForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        password: "Team@1234",
      });
      setShowTeamModal(false);
      setShowTeamCredentialsModal(true);
    } catch (error) {
      console.error("Error adding team member:", error);
      setTeamFormError(error.message || "Failed to add team member");
    } finally {
      setTeamFormLoading(false);
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    setTeamFormLoading(true);
    setTeamFormError("");

    try {
      const teamRef = doc(db, "users", editingTeamId);
      await updateDoc(teamRef, {
        firstName: teamForm.firstName,
        lastName: teamForm.lastName,
        phone: teamForm.phone,
        address: teamForm.address,
        updatedAt: serverTimestamp(),
      });

      // Refresh team members list
      const teamSnap = await getDocs(
        query(collection(db, "users"), where("userType", "==", "team")),
      );
      const teamList = teamSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTeamMembers(teamList);

      // Reset form and close modal
      setTeamForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        password: "Team@1234",
      });
      setEditingTeamId(null);
      setShowTeamModal(false);
    } catch (error) {
      console.error("Error updating team member:", error);
      setTeamFormError(error.message || "Failed to update team member");
    } finally {
      setTeamFormLoading(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (window.confirm("Are you sure you want to delete this team member?")) {
      try {
        const teamRef = doc(db, "users", teamId);
        await deleteDoc(teamRef);

        // Refresh team members list
        const teamSnap = await getDocs(
          query(collection(db, "users"), where("userType", "==", "team")),
        );
        const teamList = teamSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTeamMembers(teamList);
      } catch (error) {
        console.error("Error deleting team member:", error);
        alert("Failed to delete team member: " + error.message);
      }
    }
  };

  const handleEditTeam = (team) => {
    setTeamForm({
      firstName: team.firstName || "",
      lastName: team.lastName || "",
      email: team.email || "",
      phone: team.phone || "",
      address: team.address || "",
      password: "Team@1234",
    });
    setEditingTeamId(team.id);
    setShowTeamModal(true);
  };

  const handleAgentFormChange = (e) => {
    const { name, value } = e.target;
    setAgentForm({ ...agentForm, [name]: value });
  };

  const handleAddAgent = async (e) => {
    e.preventDefault();
    setAgentFormLoading(true);
    setAgentFormError("");

    try {
      // Create Firebase Auth user for the agent using secondary auth instance
      // This prevents logging out the current super admin
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        agentForm.email,
        agentForm.password,
      );
      const uid = userCredential.user.uid;

      // Create user document in Firestore
      await setDoc(doc(db, "users", uid), {
        firstName: agentForm.firstName,
        lastName: agentForm.lastName,
        email: agentForm.email,
        phone: agentForm.phone,
        address: agentForm.address,
        userType: "agent",
        commissionRate: parseFloat(agentForm.commissionRate) || 10,
        uid: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Sign out the agent from secondary auth (doesn't affect main auth)
      await secondaryAuth.signOut();

      // Refresh agents list
      const agentsSnap = await getDocs(
        query(collection(db, "users"), where("userType", "==", "agent")),
      );
      const agentsList = agentsSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAgents(agentsList);

      // Store credentials for display
      setNewAgentCredentials({
        email: agentForm.email,
        password: agentForm.password,
        name: `${agentForm.firstName} ${agentForm.lastName}`,
      });

      // Reset form and close modal
      setAgentForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        password: "Agent@123",
        commissionRate: 10,
      });
      setShowAgentModal(false);
      setShowCredentialsModal(true);
    } catch (error) {
      console.error("Error adding agent:", error);
      setAgentFormError(error.message || "Failed to add agent");
    } finally {
      setAgentFormLoading(false);
    }
  };

  const handleViewAgentReferrals = async (agent) => {
    // Navigate to agent details page
    navigate(`/superadmin/agents/${agent.id}`);
  };

  const handleDeleteAgent = async (agentId, agentName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete agent "${agentName}"?\n\nThis will permanently delete:\n- Agent profile from database\n- Agent login credentials\n\nThis action cannot be undone.`,
    );

    if (!confirmDelete) return;

    setDeletingAgent(agentId);

    try {
      const result = await deleteUser(agentId);

      if (result.success) {
        // Remove agent from local state
        setAgents((prev) => prev.filter((a) => a.id !== agentId));
        alert("Agent deleted successfully!");
      } else {
        alert(`Failed to delete agent: ${result.error}`);
      }
    } catch (error) {
      console.error("Error deleting agent:", error);
      alert("An error occurred while deleting the agent. Please try again.");
    } finally {
      setDeletingAgent(null);
    }
  };

  const adminCards = [
    {
      title: "User Directories",
      icon: "👥",
      path: isTeamView ? "/team/users" : "/superadmin/users",
      count: stats.users,
      description: "Control account permissions and active directories",
      bgColor: "bg-primary-50/50 border-primary-200",
      textColor: "text-primary-800",
      badgeColor: "bg-primary-100 text-primary-800",
      hoverColor: "hover:border-primary-400",
    },
    {
      title: "Partner Directory",
      icon: "🏢",
      path: isTeamView ? "/team/companies" : "/superadmin/companies",
      count: stats.companies,
      description: "Authenticate and verify corporate employers",
      bgColor: "bg-secondary-50/50 border-secondary-200",
      textColor: "text-secondary-800",
      badgeColor: "bg-secondary-100 text-secondary-800",
      hoverColor: "hover:border-secondary-400",
    },
    {
      title: "Listings Moderator",
      icon: "📋",
      path: isTeamView ? "/team/jobs" : "/superadmin/jobs",
      count: stats.jobs,
      description: "Track and moderate active employment listings",
      bgColor: "bg-secondary-50/50 border-secondary-200",
      textColor: "text-secondary-800",
      badgeColor: "bg-secondary-100 text-secondary-800",
      hoverColor: "hover:border-secondary-400",
    },
    ...(isTeamView
      ? []
      : [
          {
            title: "Subscription Tiers",
            icon: "📦",
            path: "/superadmin/packages",
            count: stats.packages,
            description: "Configure and deploy commercial package templates",
            bgColor: "bg-accent-50/50 border-accent-200",
            textColor: "text-accent-800",
            badgeColor: "bg-accent-100 text-accent-800",
            hoverColor: "hover:border-accent-400",
          },
          {
            title: "Promotional Ads",
            icon: "📢",
            path: "/superadmin/ads",
            count: stats.ads,
            description: "Review and deploy active marketing ad banners",
            bgColor: "bg-accent-50/50 border-accent-200",
            textColor: "text-accent-800",
            badgeColor: "bg-accent-100 text-accent-800",
            hoverColor: "hover:border-accent-400",
          },
          {
            title: "Platform Licenses",
            icon: "💳",
            path: "#",
            count: stats.subscriptions,
            description: "Review all active and historical account licenses",
            bgColor: "bg-primary-50/50 border-primary-200",
            textColor: "text-primary-800",
            badgeColor: "bg-primary-100 text-primary-800",
            hoverColor: "hover:border-primary-400",
            onClick: handleViewPaymentDetails,
          },
          {
            title: "Aggregate Revenue",
            icon: "💰",
            path: "/superadmin/analytics",
            count: `₹${stats.revenue.toLocaleString()}`,
            description: "Total revenue from packages",
            bgColor: "bg-secondary-50/50 border-secondary-200",
            textColor: "text-secondary-800",
            badgeColor: "bg-secondary-100 text-secondary-800",
            hoverColor: "hover:border-secondary-400",
          },
          {
            title: "Audit Transactions",
            icon: "✅",
            path: "#",
            count: paymentDetails.filter((p) => p.status === "pending").length,
            description: "Review and approve manual offline wire transfers",
            bgColor: "bg-emerald-50 border border-emerald-200",
            textColor: "text-emerald-800",
            badgeColor: "bg-emerald-100 text-emerald-800",
            hoverColor: "hover:border-emerald-400",
            onClick: handleViewPaymentDetails,
          },
          {
            title: "Partner Networks",
            icon: "🤝",
            path: "#",
            count: agents.length,
            description: "Oversee channel agents and their registered referrals",
            bgColor: "bg-primary-50/50 border-primary-200",
            textColor: "text-primary-800",
            badgeColor: "bg-primary-100 text-primary-800",
            hoverColor: "hover:border-primary-400",
            onClick: () => setShowAgentListModal(true),
          },
        ]),
    ...(isTeamView
      ? []
      : [
          {
            title: "Staff Roster",
            icon: "👔",
            path: "#",
            count: teamMembers.length,
            description: "Coordinate and oversee internal administrator accounts",
            bgColor: "bg-primary-50/50 border-primary-200",
            textColor: "text-primary-800",
            badgeColor: "bg-primary-100 text-primary-800",
            hoverColor: "hover:border-primary-400",
            onClick: () => setShowTeamListModal(true),
          },
          {
            title: "Branch Operations",
            icon: "🏪",
            path: "/superadmin/franchises",
            count: stats.franchises || 0,
            description: "Supervise active regional franchise entities",
            bgColor: "bg-accent-50/50 border-accent-200",
            textColor: "text-accent-800",
            badgeColor: "bg-accent-100 text-accent-800",
            hoverColor: "hover:border-accent-400",
          },
        ]),
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Rebranded Elegant Header */}
      <div className="bg-gradient-to-r from-primary-900 via-primary-800 to-slate-900 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-20 translate-x-20 pointer-events-none"></div>
        <div className="relative z-10 space-y-2">
          <h1 className="text-4xl font-black tracking-tight">
            {isTeamView ? "Operations Command Center (Staff View)" : "Operations Command Center"}
          </h1>
          <p className="text-slate-300 text-base max-w-2xl">
            {isTeamView
              ? "Platform administration, moderator tracking, and operation insights dashboard."
              : "System-wide tracking, administrative controls, and performance metrics."}
          </p>
        </div>
      </div>

      {/* Redesigned Metric Sections */}
      <div>
        <h2 className="text-xs font-extrabold text-slate-400 uppercase tracking-widest mb-4">Platform Scale Indicators</h2>
        <div className={`grid grid-cols-1 ${isTeamView ? "md:grid-cols-3" : "md:grid-cols-3"} gap-6`}>
          <div
            className="bg-white rounded-2xl p-6 border-2 border-slate-100 shadow-md cursor-pointer hover:border-primary-500 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
            onClick={() =>
              navigate(isTeamView ? "/team/users" : "/superadmin/users")
            }
          >
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-slate-400 text-xs font-extrabold uppercase tracking-wide">
                  Active Platform Users
                </p>
                <p className="text-3xl font-black text-slate-800">{stats.users}</p>
                <p className="text-slate-500 text-xs font-medium">
                  Total accounts active
                </p>
              </div>
              <div className="w-12 h-12 bg-primary-50 text-primary-600 rounded-2xl flex items-center justify-center group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-2xl p-6 border-2 border-slate-100 shadow-md cursor-pointer hover:border-secondary-500 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
            onClick={() =>
              navigate(isTeamView ? "/team/companies" : "/superadmin/companies")
            }
          >
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-slate-400 text-xs font-extrabold uppercase tracking-wide">
                  Corporate Partners
                </p>
                <p className="text-3xl font-black text-slate-800">{stats.companies}</p>
                <p className="text-secondary-600 text-xs font-extrabold bg-secondary-50 px-2 py-0.5 rounded-full w-fit">
                  {stats.pendingCompanies} awaiting audit
                </p>
              </div>
              <div className="w-12 h-12 bg-secondary-50 text-secondary-600 rounded-2xl flex items-center justify-center group-hover:bg-secondary-600 group-hover:text-white transition-colors duration-300">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-2xl p-6 border-2 border-slate-100 shadow-md cursor-pointer hover:border-secondary-500 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
            onClick={() =>
              navigate(isTeamView ? "/team/jobs" : "/superadmin/jobs")
            }
          >
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-slate-400 text-xs font-extrabold uppercase tracking-wide">
                  Live Opportunities
                </p>
                <p className="text-3xl font-black text-slate-800">{stats.activeJobs}</p>
                <p className="text-slate-500 text-xs font-medium">
                  {stats.jobs} currently indexed listings
                </p>
              </div>
              <div className="w-12 h-12 bg-secondary-50 text-secondary-600 rounded-2xl flex items-center justify-center group-hover:bg-secondary-600 group-hover:text-white transition-colors duration-300">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M17 20H7m10 0v-2a3 3 0 00-5.356-1.857M17 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!isTeamView && (
        <div>
          <h2 className="text-xs font-extrabold text-slate-400 uppercase tracking-widest mb-4">Financial Operations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div
              className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-100 shadow-md cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
              onClick={handleViewPaymentDetails}
            >
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-emerald-800 text-xs font-extrabold uppercase tracking-wide">Aggregate Revenue</p>
                  <p className="text-3xl font-black text-emerald-950">
                    ₹{stats.revenue.toLocaleString()}
                  </p>
                  <p className="text-emerald-700 text-xs font-semibold">
                    ₹{stats.monthlyRevenue.toLocaleString()} earned this cycle
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/80 border border-emerald-200 text-emerald-700 rounded-2xl flex items-center justify-center shadow-sm group-hover:bg-emerald-600 group-hover:text-white transition-colors duration-300">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div
              className="bg-gradient-to-br from-accent-50 to-orange-50 rounded-2xl p-6 border-2 border-accent-100 shadow-md cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group"
              onClick={() => navigate("/superadmin/franchises")}
            >
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-accent-800 text-xs font-extrabold uppercase tracking-wide">Branch Operations</p>
                  <p className="text-3xl font-black text-accent-950">
                    {stats.franchises || 0}
                  </p>
                  <p className="text-accent-700 text-xs font-semibold">
                    Active regional franchise entities
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/80 border border-accent-200 text-accent-700 rounded-2xl flex items-center justify-center shadow-sm group-hover:bg-accent-600 group-hover:text-white transition-colors duration-300">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Redesigned Quick Actions Panel */}
      <div>
        <h2 className="text-xs font-extrabold text-slate-400 uppercase tracking-widest mb-4">Operations Control Center</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adminCards.map((card, index) => (
            <div
              key={index}
              onClick={() =>
                card.onClick ? card.onClick() : navigate(card.path)
              }
              className={`${card.bgColor} rounded-3xl p-5 cursor-pointer hover:shadow-xl transition-all duration-300 border-2 ${card.hoverColor} transform hover:-translate-y-1 flex flex-col justify-between h-40`}
            >
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-2xl shadow-sm border border-slate-100">
                  {card.icon}
                </div>
                <div className="text-slate-400">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
              <div className="space-y-1">
                <h3 className={`${card.textColor} font-black text-base`}>
                  {card.title}
                </h3>
                <p className="text-slate-500 text-xs line-clamp-1">
                  {card.description}
                </p>
              </div>
              <div className="mt-2">
                {card.count !== undefined && (
                  <div
                    className={`${card.badgeColor} px-3 py-1 rounded-full text-xs font-bold inline-block shadow-sm`}
                  >
                    {card.title === "Aggregate Revenue"
                      ? card.count
                      : `Total: ${card.count}`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity Grid */}
      {/* Recent Activity Grid */}
      {!isTeamView && (
        <div className="space-y-6">
          {/* Top Row: Jobs & Companies */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recently Published Roles */}
            <div className="bg-white rounded-3xl border-2 border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center text-primary-600">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 20H7m10 0v-2a3 3 0 00-5.356-1.857M16 20H2v-2a3 3 0 015.356-1.857M7 20H2v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-black text-slate-800">
                    Recently Published Roles
                  </h3>
                </div>
                <button
                  onClick={() => navigate("/superadmin/jobs")}
                  className="text-primary-600 hover:text-primary-850 text-xs font-bold transition-colors"
                >
                  View All →
                </button>
              </div>

              {recentJobs.length > 0 ? (
                <div className="space-y-3">
                  {recentJobs.map((job) => (
                    <div
                      key={job.id}
                      className="p-4 bg-slate-50/50 rounded-2xl border border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 mr-2">
                          <h4 className="font-bold text-slate-800 text-sm mb-0.5 truncate">
                            {job.title}
                          </h4>
                          <p className="text-xs text-slate-500 mb-2 truncate">
                            {job.companyDetails?.name ||
                              job.company ||
                              "Unknown"}
                          </p>
                          <div className="flex items-center gap-3 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                            <span>
                              📍{" "}
                              {typeof job.location === "object"
                                ? `${job.location.city || ""}`
                                : job.location || "N/A"}
                            </span>
                            <span>👥 {job.applicationCount || 0} Applications</span>
                          </div>
                        </div>
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase ${
                            job.status === "active"
                              ? "bg-green-50 text-green-700 border border-green-200"
                              : job.status === "closed"
                                ? "bg-red-50 text-red-700 border border-red-200"
                                : "bg-yellow-50 text-yellow-700 border border-yellow-200"
                          }`}
                        >
                          {job.status?.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="text-slate-300 text-4xl mb-2">💼</div>
                  <p className="text-slate-500 text-sm font-medium">No live opportunity listings recorded.</p>
                </div>
              )}
            </div>

            {/* Leading Employer Brands */}
            <div className="bg-white rounded-3xl border-2 border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-secondary-50 rounded-xl flex items-center justify-center text-secondary-600">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-black text-slate-800">
                    Leading Employer Brands
                  </h3>
                </div>
                <button
                  onClick={() => navigate("/superadmin/companies")}
                  className="text-secondary-600 hover:text-secondary-850 text-xs font-bold transition-colors"
                >
                  View All →
                </button>
              </div>

              {topCompanies.length > 0 ? (
                <div className="space-y-3">
                  {topCompanies.map((company, index) => (
                    <div
                      key={company.id}
                      className="flex items-center gap-3 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-secondary-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-extrabold text-xs flex-shrink-0 shadow-sm">
                        #{index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-slate-800 text-sm truncate">
                          {company.name}
                        </h4>
                        <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-wide truncate">
                          {company.industry || "N/A"}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0 bg-secondary-50 border border-secondary-100 px-3 py-1 rounded-xl">
                        <div className="text-sm font-black text-secondary-700">
                          {company.jobCount}
                        </div>
                        <div className="text-[9px] text-secondary-500 font-bold uppercase tracking-wider">listings</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="text-slate-300 text-4xl mb-2">🏢</div>
                  <p className="text-slate-500 text-sm font-medium">No corporate partner profiles registered.</p>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Row: Ads & Revenue */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Promo Deployments */}
            <div className="bg-white rounded-3xl border-2 border-slate-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent-50 rounded-xl flex items-center justify-center text-accent-600">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-black text-slate-800">
                    Recent Promo Deployments
                  </h3>
                </div>
                <button
                  onClick={() => navigate("/superadmin/ads")}
                  className="text-accent-600 hover:text-accent-850 text-xs font-bold transition-colors"
                >
                  View All →
                </button>
              </div>

              {recentAds.length > 0 ? (
                <div className="space-y-3">
                  {recentAds.map((ad) => (
                    <div
                      key={ad.id}
                      className="flex gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-16 h-12 bg-slate-200 rounded-xl flex-shrink-0 overflow-hidden shadow-sm">
                        <img
                          src={ad.imageUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4
                          className="font-bold text-slate-800 text-sm mb-0.5 truncate"
                          title={ad.fileName}
                        >
                          {ad.fileName || "Untitled Campaign"}
                        </h4>
                        <p className="text-xs text-slate-500 mb-1.5 truncate">
                          {ad.userName || "Admin Operations"}
                        </p>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[9px] font-extrabold tracking-wider uppercase ${
                            ad.status === "active"
                              ? "bg-green-50 text-green-700 border border-green-200"
                              : "bg-slate-200/50 text-slate-650"
                          }`}
                        >
                          {ad.status?.toUpperCase() || "INACTIVE"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="text-slate-300 text-4xl mb-2">📢</div>
                  <p className="text-slate-500 text-sm font-medium">No marketing ad banner campaigns found.</p>
                </div>
              )}
            </div>

            {/* Financial Growth Insights */}
            <div className="bg-white rounded-3xl border-2 border-slate-100 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-black text-slate-800">
                  Financial Growth Insights
                </h3>
              </div>
              <div className="h-[300px] w-full">
                {revenueData.labels.length > 0 && (
                  <Line
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          mode: "index",
                          intersect: false,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          titleColor: "#1f2937",
                          bodyColor: "#4b5563",
                          borderColor: "#e5e7eb",
                          borderWidth: 1,
                          callbacks: {
                            label: function (context) {
                              let label = context.dataset.label || "";
                              if (label) {
                                label += ": ";
                              }
                              if (context.parsed.y !== null) {
                                label += new Intl.NumberFormat("en-IN", {
                                  style: "currency",
                                  currency: "INR",
                                }).format(context.parsed.y);
                              }
                              return label;
                            },
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: { borderDash: [2, 2], color: "#f3f4f6" },
                          ticks: {
                            font: { size: 10 },
                            callback: (value) => "₹" + value / 1000 + "k",
                          },
                        },
                        x: {
                          grid: { display: false },
                          ticks: { font: { size: 10 } },
                        },
                      },
                    }}
                    data={revenueData}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Agent Modal */}
      {showAgentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 transform transition-all duration-300 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black text-slate-850">
                Onboard Channel Agent
              </h2>
              <button
                onClick={() => setShowAgentModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleAddAgent} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={agentForm.firstName}
                    onChange={handleAgentFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={agentForm.lastName}
                    onChange={handleAgentFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={agentForm.email}
                  onChange={handleAgentFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={agentForm.phone}
                  onChange={handleAgentFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  name="address"
                  value={agentForm.address}
                  onChange={handleAgentFormChange}
                  required
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    type="text"
                    name="password"
                    value={agentForm.password}
                    onChange={handleAgentFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter password"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Default: Agent@123
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Commission Rate (%)
                  </label>
                  <input
                    type="number"
                    name="commissionRate"
                    value={agentForm.commissionRate}
                    onChange={handleAgentFormChange}
                    required
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., 10"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Commission percentage
                  </p>
                </div>
              </div>

              {agentFormError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="text-red-700 text-sm font-medium">
                    {agentFormError}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAgentModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={agentFormLoading}
                  className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {agentFormLoading ? "Adding..." : "Add Agent"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Agents List Modal */}
      {showAgentListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full p-8 transform transition-all duration-300 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">All Agents</h2>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowAgentListModal(false);
                    setShowAgentModal(true);
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Agent
                </button>
                <button
                  onClick={() => setShowAgentListModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {agents.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Name
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Email
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Phone
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Address
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Joined
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((agent, index) => (
                      <tr
                        key={agent.id}
                        className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                              {agent.firstName?.charAt(0)?.toUpperCase() || "A"}
                            </div>
                            <p className="font-medium text-gray-900">
                              {agent.firstName} {agent.lastName}
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {agent.email}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {agent.phone}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {agent.address}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {agent.createdAt?.toDate?.()?.toLocaleDateString() ||
                            "N/A"}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewAgentReferrals(agent)}
                              className="px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold rounded-lg transition-colors text-sm flex items-center gap-2"
                              title="View Referrals"
                            >
                              <span className="px-2 py-0.5 bg-indigo-600 text-white rounded-full text-xs font-bold">
                                {agent.referralCount || 0}
                              </span>
                              Referrals
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteAgent(
                                  agent.id,
                                  `${agent.firstName} ${agent.lastName}`,
                                )
                              }
                              disabled={deletingAgent === agent.id}
                              className={`p-2 rounded-lg transition-colors ${
                                deletingAgent === agent.id
                                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                  : "bg-red-100 hover:bg-red-200 text-red-600"
                              }`}
                              title="Delete Agent"
                            >
                              {deletingAgent === agent.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                              ) : (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🤝</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No agents yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Add your first agent to start tracking referrals.
                </p>
                <button
                  onClick={() => setShowAgentModal(true)}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add First Agent
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agent Credentials Success Modal */}
      {showCredentialsModal && newAgentCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all duration-300">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Agent Created Successfully!
              </h2>
              <p className="text-gray-600">
                Share these credentials with {newAgentCredentials.name}
              </p>
            </div>

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-6 border border-indigo-200">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email (Login ID)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newAgentCredentials.email}
                      readOnly
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          newAgentCredentials.email,
                        );
                      }}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                      title="Copy email"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newAgentCredentials.password}
                      readOnly
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          newAgentCredentials.password,
                        );
                      }}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                      title="Copy password"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex gap-3">
                <svg
                  className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-yellow-800 mb-1">
                    Important!
                  </p>
                  <p className="text-sm text-yellow-700">
                    Make sure to save these credentials. The agent will use them
                    to login at the login page.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setShowCredentialsModal(false);
                setNewAgentCredentials(null);
              }}
              className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors"
            >
              Got it, Close
            </button>
          </div>
        </div>
      )}

      {/* Payment Details Modal */}
      {showPaymentDetails &&
        (() => {
          const filteredPayments = getFilteredPayments();
          const filteredRevenue = filteredPayments.reduce(
            (sum, p) => sum + (p.totalAmount || 0),
            0,
          );
          return (
            <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-md flex items-center justify-center z-50 p-4 sm:p-6 animate-in fade-in duration-200">
              <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl border border-slate-200/80 max-w-6xl w-full p-6 sm:p-8 max-h-[90vh] overflow-y-auto space-y-6 animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-100 pb-5">
                  <div>
                    <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
                      <span>💳</span> Financial Settlements Ledger
                    </h2>
                    <p className="text-sm text-slate-500 mt-1.5 font-medium">
                      Auditing{" "}
                      <span className="font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-lg border border-indigo-100">
                        {filteredPayments.length}
                      </span>{" "}
                      matching invoicing actions of {paymentDetails.length} total transactions &nbsp;·&nbsp;
                      Aggregated Value:{" "}
                      <span className="font-extrabold text-emerald-755">
                        ₹{filteredRevenue.toLocaleString()}
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <button
                      onClick={exportToCSV}
                      className="flex items-center gap-2 px-4.5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-2xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm"
                      title="Export filtered data to CSV"
                    >
                      <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      CSV Export
                    </button>
                    <button
                      onClick={exportToExcel}
                      className="flex items-center gap-2 px-4.5 py-2.5 bg-slate-105 hover:bg-slate-200 text-slate-700 font-bold rounded-2xl transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5 text-sm border border-slate-200"
                      title="Export filtered data to Excel"
                    >
                      <svg className="w-4 h-4 text-indigo-650" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Excel Sheet
                    </button>
                    <button
                      onClick={() => {
                        setShowPaymentDetails(false);
                      }}
                      className="text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* ── FILTER BAR ── */}
                <div className="bg-slate-50/80 rounded-3xl border border-slate-200/80 p-5 space-y-5 shadow-inner">
                  {/* Row 1: Date Range Quick Buttons */}
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-xs font-bold text-slate-550 uppercase tracking-wider mr-1">
                      📅 Date Range:
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {[
                        {
                          key: "all",
                          label: "All Time",
                          bg: "bg-slate-900",
                          ring: "ring-slate-300",
                        },
                        {
                          key: "today",
                          label: "Today",
                          bg: "bg-sky-650",
                          ring: "ring-sky-300",
                        },
                        {
                          key: "last7",
                          label: "Last 7 Days",
                          bg: "bg-indigo-655",
                          ring: "ring-indigo-300",
                        },
                        {
                          key: "lastMonth",
                          label: "Last Month",
                          bg: "bg-amber-600",
                          ring: "ring-amber-300",
                        },
                        {
                          key: "custom",
                          label: "Custom Period",
                          bg: "bg-rose-600",
                          ring: "ring-rose-300",
                        },
                      ].map((btn) => (
                        <button
                          key={btn.key}
                          onClick={() => setPaymentFilterRange(btn.key)}
                          className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all duration-200 ${
                            paymentFilterRange === btn.key
                              ? `${btn.bg} text-white shadow-sm ring-2 ${btn.ring} scale-105`
                              : "bg-white text-slate-655 hover:bg-slate-100 border border-slate-200/80"
                          }`}
                        >
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Date Inputs — shown only when 'custom' is selected */}
                  {paymentFilterRange === "custom" && (
                    <div className="flex flex-wrap items-center gap-4 pl-1 animate-in slide-in-from-top-2 duration-200">
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-slate-550 uppercase tracking-wider">
                          From:
                        </label>
                        <input
                          type="date"
                          value={paymentFilterCustomStart}
                          onChange={(e) =>
                            setPaymentFilterCustomStart(e.target.value)
                          }
                          className="px-3.5 py-1.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-450 bg-white shadow-inner font-medium text-slate-800"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-bold text-slate-550 uppercase tracking-wider">
                          To:
                        </label>
                        <input
                          type="date"
                          value={paymentFilterCustomEnd}
                          onChange={(e) =>
                            setPaymentFilterCustomEnd(e.target.value)
                          }
                          className="px-3.5 py-1.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-450 bg-white shadow-inner font-medium text-slate-800"
                        />
                      </div>
                    </div>
                  )}

                  {/* Row 2: User Type + Search */}
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pt-1">
                    {/* User Type Filter */}
                    <div className="flex flex-wrap items-center gap-2.5">
                      <span className="text-xs font-bold text-slate-550 uppercase tracking-wider mr-1">
                        👤 Platform Tier:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { key: "all", label: "All Tiers", bg: "bg-slate-900" },
                          {
                            key: PACKAGE_TYPES.JOB_SEEKER,
                            label: "Candidate Tier",
                            bg: "bg-blue-600",
                          },
                          {
                            key: PACKAGE_TYPES.EMPLOYER,
                            label: "Enterprise Tier",
                            bg: "bg-purple-600",
                          },
                          {
                            key: PACKAGE_TYPES.FRANCHISE,
                            label: "Franchise Tier",
                            bg: "bg-orange-650",
                          },
                          {
                            key: PACKAGE_TYPES.DATABASE,
                            label: "Registry Seeker",
                            bg: "bg-emerald-600",
                          },
                        ].map((btn) => (
                          <button
                            key={btn.key}
                            onClick={() => setPaymentFilterUserType(btn.key)}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 border ${
                              paymentFilterUserType === btn.key
                                ? `${btn.bg} text-white border-transparent shadow-sm scale-105`
                                : "bg-white text-slate-655 border-slate-200 hover:bg-slate-100"
                            }`}
                          >
                            {btn.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Search */}
                    <div className="flex-1 max-w-md">
                      <div className="relative">
                        <svg
                          className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        <input
                          type="text"
                          placeholder="Identify transactions by user, email, or tier..."
                          value={paymentSearchQuery}
                          onChange={(e) =>
                            setPaymentSearchQuery(e.target.value)
                          }
                          className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-650 bg-white shadow-inner font-medium text-slate-800"
                        />
                      </div>
                    </div>

                    {/* Clear Filters */}
                    {(paymentFilterRange !== "all" ||
                      paymentFilterUserType !== "all" ||
                      paymentSearchQuery) && (
                      <button
                        onClick={() => {
                          setPaymentFilterRange("all");
                          setPaymentFilterUserType("all");
                          setPaymentFilterCustomStart("");
                          setPaymentFilterCustomEnd("");
                          setPaymentSearchQuery("");
                        }}
                        className="px-4 py-1.5 bg-rose-50 border border-rose-100 hover:bg-rose-100 text-rose-700 rounded-full text-xs font-bold transition-colors"
                      >
                        ✕ Clear Filters
                      </button>
                    )}
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-2xl border border-slate-200/80 shadow-md">
                  <table className="w-full">
                    <thead className="bg-slate-950 text-white">
                      <tr>
                        <th className="text-left py-4.5 px-5 font-bold text-xs uppercase tracking-wider rounded-tl-2xl">
                          Identity Record
                        </th>
                        <th className="text-left py-4.5 px-5 font-bold text-xs uppercase tracking-wider">
                          Invoicing Destination
                        </th>
                        <th className="text-left py-4.5 px-5 font-bold text-xs uppercase tracking-wider">
                          Platform Tier
                        </th>
                        <th className="text-left py-4.5 px-5 font-bold text-xs uppercase tracking-wider">
                          Settled Amount
                        </th>
                        <th className="text-left py-4.5 px-5 font-bold text-xs uppercase tracking-wider">
                          Subscribed Package
                        </th>
                        <th className="text-left py-4.5 px-5 font-bold text-xs uppercase tracking-wider rounded-tr-2xl">
                          Filing Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-20 text-center bg-white/50 backdrop-blur-sm">
                            <div className="text-slate-350 text-5xl mb-3">
                              🔍
                            </div>
                            <p className="text-slate-800 font-extrabold text-base">
                              No invoicing records match current filter criteria
                            </p>
                            <p className="text-slate-450 text-xs mt-1 font-medium">
                              Try expanding the date range or clearing custom search parameters.
                            </p>
                          </td>
                        </tr>
                      ) : (
                        filteredPayments.map((payment, index) => (
                          <tr
                            key={payment.id}
                            className={`border-b border-slate-100 hover:bg-indigo-50/40 transition-colors ${
                              index % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                            }`}
                          >
                            <td
                              className="py-4 px-5 cursor-pointer group"
                              onClick={() => {
                                navigate(`/admin/user/${payment.userId}`);
                              }}
                              title="Click to view full user information"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-gradient-to-tr from-slate-900 via-indigo-950 to-slate-900 rounded-full flex items-center justify-center text-white font-extrabold text-xs shadow-md border border-slate-800 group-hover:scale-105 transition-transform duration-200">
                                  {payment.userName?.charAt(0)?.toUpperCase() || "U"}
                                </div>
                                <div className="flex flex-col">
                                  <span className="font-bold text-slate-900 group-hover:text-indigo-650 transition-colors">
                                    {payment.userName || "Unknown"}
                                  </span>
                                  {payment.userId && (
                                    <span className="text-[9px] text-slate-400 font-mono tracking-tight mt-0.5">
                                      ID: {payment.userId.substring(0, 8)}...
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-5 text-slate-600 text-xs sm:text-sm font-semibold">
                              {payment.userEmail || "No email"}
                            </td>
                            <td className="py-4 px-5">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-bold border inline-flex items-center gap-1.5 ${
                                  payment.userType === "job_seeker" ||
                                  payment.userType === "jobseeker" ||
                                  payment.userType === "seeker"
                                    ? "bg-blue-500/10 text-blue-600 border-blue-500/20"
                                    : payment.userType === "employer"
                                      ? "bg-purple-500/10 text-purple-600 border-purple-500/20"
                                      : payment.userType === "franchise"
                                        ? "bg-orange-500/10 text-orange-655 border-orange-500/20"
                                        : payment.userType === "database"
                                          ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                                          : payment.userType === "agent"
                                            ? "bg-teal-500/10 text-teal-600 border-teal-500/20"
                                            : payment.userType === "team"
                                              ? "bg-cyan-500/10 text-cyan-600 border-cyan-500/20"
                                              : "bg-slate-500/10 text-slate-600 border-slate-500/20"
                                }`}
                              >
                                {payment.userType === "job_seeker" ||
                                payment.userType === "jobseeker" ||
                                payment.userType === "seeker"
                                  ? "👤 Candidate"
                                  : payment.userType === "employer"
                                    ? "🏢 Enterprise"
                                    : payment.userType === "franchise"
                                      ? "🏪 Franchise"
                                      : payment.userType === "database"
                                        ? "🗄️ Registry"
                                        : payment.userType === "agent"
                                          ? "🤝 Partner"
                                          : payment.userType === "team"
                                            ? "👔 Operations"
                                            : `❓ ${payment.userType || "Other"}`}
                              </span>
                            </td>
                            <td className="py-4 px-5">
                              <span className="font-extrabold text-slate-900 text-base">
                                ₹{payment.totalAmount?.toLocaleString() || "0"}
                              </span>
                            </td>
                            <td className="py-4 px-5">
                              <span className="px-2.5 py-1 bg-indigo-500/10 text-indigo-700 rounded-xl text-xs font-bold border border-indigo-500/20">
                                {payment.packageName || "Unknown"}
                              </span>
                            </td>
                            <td className="py-4 px-5 text-slate-550 text-xs font-semibold">
                              {payment.date?.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }) || "Unknown"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Add Team Member Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 transform transition-all duration-300 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingTeamId ? "Edit Team Member" : "Add New Team Member"}
              </h2>
              <button
                onClick={() => {
                  setShowTeamModal(false);
                  setEditingTeamId(null);
                  setTeamForm({
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    address: "",
                    password: "Team@1234",
                  });
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form
              onSubmit={editingTeamId ? handleUpdateTeam : handleAddTeam}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={teamForm.firstName}
                    onChange={handleTeamFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={teamForm.lastName}
                    onChange={handleTeamFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={teamForm.email}
                  onChange={handleTeamFormChange}
                  required
                  disabled={editingTeamId}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={teamForm.phone}
                  onChange={handleTeamFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Address
                </label>
                <textarea
                  name="address"
                  value={teamForm.address}
                  onChange={handleTeamFormChange}
                  required
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter address"
                />
              </div>

              {!editingTeamId && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    type="text"
                    name="password"
                    value={teamForm.password}
                    onChange={handleTeamFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter password"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Default: Team@1234
                  </p>
                </div>
              )}

              {teamFormError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="text-red-700 text-sm font-medium">
                    {teamFormError}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowTeamModal(false);
                    setEditingTeamId(null);
                    setTeamForm({
                      firstName: "",
                      lastName: "",
                      email: "",
                      phone: "",
                      address: "",
                      password: "Team@1234",
                    });
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={teamFormLoading}
                  className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {teamFormLoading
                    ? "Processing..."
                    : editingTeamId
                      ? "Update Member"
                      : "Add Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Team Members List Modal */}
      {showTeamListModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full p-8 transform transition-all duration-300 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowTeamListModal(false);
                    setShowTeamModal(true);
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Team Member
                </button>
                <button
                  onClick={() => setShowTeamListModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {teamMembers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Name
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Email
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Phone
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Address
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Joined
                      </th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamMembers.map((team, index) => (
                      <tr
                        key={team.id}
                        className={`border-b border-gray-100 ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                              {team.firstName?.charAt(0)?.toUpperCase() || "T"}
                            </div>
                            <p className="font-medium text-gray-900">
                              {team.firstName} {team.lastName}
                            </p>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {team.email}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {team.phone}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {team.address}
                        </td>
                        <td className="py-4 px-4 text-gray-700">
                          {team.createdAt?.toDate?.()?.toLocaleDateString() ||
                            "N/A"}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditTeam(team)}
                              className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold rounded-lg transition-colors text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteTeam(team.id)}
                              className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 font-semibold rounded-lg transition-colors text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">👔</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No team members yet
                </h3>
                <p className="text-gray-500 mb-4">
                  Add your first team member to get started.
                </p>
                <button
                  onClick={() => {
                    setShowTeamListModal(false);
                    setShowTeamModal(true);
                  }}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add First Team Member
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Team Credentials Success Modal */}
      {showTeamCredentialsModal && newTeamCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 transform transition-all duration-300">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Team Member Created Successfully!
              </h2>
              <p className="text-gray-600">
                Share these credentials with {newTeamCredentials.name}
              </p>
            </div>

            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 mb-6 border border-cyan-200">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email (Login ID)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newTeamCredentials.email}
                      readOnly
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(newTeamCredentials.email);
                      }}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                      title="Copy email"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newTeamCredentials.password}
                      readOnly
                      className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 font-mono text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          newTeamCredentials.password,
                        );
                      }}
                      className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                      title="Copy password"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
              <div className="flex gap-3">
                <svg
                  className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-yellow-800 mb-1">
                    Important!
                  </p>
                  <p className="text-sm text-yellow-700">
                    Make sure to save these credentials. The team member will
                    use them to login at the login page.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                setShowTeamCredentialsModal(false);
                setNewTeamCredentials(null);
              }}
              className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors"
            >
              Got it, Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
